import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, LogOut, Copy, Check, ExternalLink, Wallet, RefreshCw, Shield, ChevronRight, Power, Loader } from 'lucide-react';
import { useWalletBalances } from '../services/walletBalanceService';
import { getUserTradingWallets, TradingWallet, restoreTradingWalletKeypair } from '../services/tradingWalletService';
import { updateWalletConnection, getUserProfile, updateActiveTradingWallet, notifyActiveWalletChanged } from '../services/userProfileService';
import { getBalance, getTokenBalances } from '../services/shyftService';
import { monitorWallet, stopMonitoringWallet } from '../services/walletMonitorService';
import { getWalletTokensWithMetadata, getBatchWalletTokensWithMetadata } from '../services/solanatrackerService';
import { wsService } from '../services/websocketService';

// Add type definitions for Phantom and Solflare wallets
interface WalletAdapter {
  publicKey: { toString(): string };
  isConnected: boolean;
  on(event: string, callback: (...args: any[]) => void): void;
  removeListener(event: string, callback: (...args: any[]) => void): void;
  disconnect(): Promise<void>;
}

// Extend Window interface to include wallet adapters and our custom properties
declare global {
  interface Window {
    solana?: WalletAdapter & { isPhantom?: boolean };
    solflare?: WalletAdapter;
  }
}

// Utility to notify wallet connection component of new trading wallet creation
export const notifyNewTradingWallet = () => {
  const event = new CustomEvent('ekko:trading_wallet_created');
  window.dispatchEvent(event);
};

// Utility to notify wallet connection component of trading wallet deletion
export const notifyTradingWalletDeleted = () => {
  const event = new CustomEvent('ekko:trading_wallet_deleted');
  window.dispatchEvent(event);
};

// Add custom CSS for the wallet connection component
const walletConnectionStyles = `
  /* Glassmorphism button effect with white theme */
  .wallet-button {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(36, 195, 91, 0.3);
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    color: #1f2937;
  }
  
  .wallet-button:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(36, 195, 91, 0.5);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  /* Dropdown styling with white glassmorphism theme */
  .wallet-dropdown {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(36, 195, 91, 0.2);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    max-height: 80vh;
    overflow: hidden;
  }
  
  /* Custom scrollbar for the dropdown - white theme */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(229, 231, 235, 0.5);
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(36, 195, 91, 0.4);
    border-radius: 3px;
    transition: all 0.2s;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(36, 195, 91, 0.6);
  }

  /* Firefox scrollbar styling */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(36, 195, 91, 0.4) rgba(229, 231, 235, 0.5);
  }
  
  /* Section divider with gradient - with updated color */
  .section-divider {
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(36, 195, 91, 0.3), transparent);
    margin: 0.5rem 0;
  }
  
  /* Token highlight effect - with updated colors */
  .token-highlight {
    background: linear-gradient(to right, rgba(36, 195, 91, 0.15), rgba(36, 195, 91, 0.05));
    border: 1px solid rgba(36, 195, 91, 0.3);
  }
  
  /* Balance update animation - with updated color */
  @keyframes pulse-green {
    0%, 100% { background-color: rgba(36, 195, 91, 0.05); }
    50% { background-color: rgba(36, 195, 91, 0.2); }
  }
  
  .animate-pulse-green {
    animation: pulse-green 2s ease-in-out;
  }
  
  /* Header section with white glassmorphism */
  .wallet-header {
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid rgba(36, 195, 91, 0.2);
    backdrop-filter: blur(5px);
    padding: 8px 10px;
  }
  
  /* Main content area with white glassmorphism */
  .wallet-content {
    background: rgba(255, 255, 255, 0.9);
    padding: 10px;
  }
  
  /* Glassmorphism background for balance section */
  .balance-section {
    background: rgba(255, 255, 255, 0.95);
  }
  
  /* Glassmorphism background for item rows */
  .wallet-item {
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 0;
    margin-bottom: 1px;
  }
  
  /* Individual token items */
  .token-item {
    background: rgba(255, 255, 255, 0.8) !important;
    border: none !important;
    margin-bottom: 1px;
  }
  
  .token-item:hover {
    background: rgba(255, 255, 255, 0.95) !important;
  }
  
  /* Trading wallet container */
  .trading-wallet-container {
    margin-top: 8px;
  }
  
  /* Trading wallet header */
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
    padding: 0;
  }
  
  /* Trading wallet item */
  .trading-wallet-item {
    background: rgba(255, 255, 255, 0.8) !important;
    border: none !important;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
  }
  
  .trading-wallet-item:hover {
    background: rgba(255, 255, 255, 0.95) !important;
    border-color: rgba(36, 195, 91, 0.2);
  }
  
  .trading-wallet-item.active-wallet {
    background: rgba(36, 195, 91, 0.1);
    border-color: rgba(36, 195, 91, 0.3);
  }
  
  .active-wallet-indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: #24c35b;
    box-shadow: 0 0 8px rgba(36, 195, 91, 0.5);
  }
  
  .activate-button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    background: rgba(36, 195, 91, 0.1);
    border: 1px solid rgba(36, 195, 91, 0.2);
    color: #24c35b;
    font-size: 11px;
    transition: all 0.2s ease;
  }
  
  .activate-button:hover {
    background: rgba(36, 195, 91, 0.2);
    border-color: rgba(36, 195, 91, 0.3);
  }
  
  .activate-button.active {
    background: rgba(36, 195, 91, 0.2);
    border-color: rgba(36, 195, 91, 0.4);
  }
  
  .activate-button-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .activate-button-text {
    font-weight: 500;
  }
  
  /* Remove padding completely */
  .wallet-menu {
    padding: 0;
    margin: 0;
  }
  
  /* Individual items without any margin/padding */
  .flush-item {
    margin: 0;
    border-radius: 0;
  }

  .token-balances-section {
    max-height: 192px; /* Equivalent to max-h-48 (12rem = 192px) */
    overflow-y: auto;
  }

  .wallet-tabs {
    display: flex;
    gap: 1px;
    background: rgba(229, 231, 235, 0.3);
    padding: 2px;
    border-radius: 6px;
    margin-bottom: 12px;
  }

  .wallet-tab {
    flex: 1;
    text-align: center;
    padding: 6px 12px;
    font-size: 12px;
    color: rgba(75, 85, 99, 0.8);
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .wallet-tab:hover {
    color: rgba(55, 65, 81, 1);
  }

  .wallet-tab.active {
    background: rgba(36, 195, 91, 0.1);
    color: #24c35b;
    font-weight: 500;
  }

  .token-item {
    padding: 8px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background-color 0.15s;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .token-item:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .token-value {
    text-align: right;
    font-family: 'JetBrains Mono', monospace;
  }

  .animate-pulse-green {
    animation: pulse-green 1.5s;
  }

  @keyframes pulse-green {
    0% { background-color: transparent; }
    30% { background-color: rgba(36, 195, 91, 0.3); }
    100% { background-color: transparent; }
  }

  .wallet-actions {
    padding: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    width: 100%;
  }

  /* Trading Wallet List */
  .trading-wallet-list {
    max-height: 180px;
    overflow-y: auto;
  }
`;

interface WalletConnectionProps {
  className?: string;
  walletAddress?: string;
  walletProvider?: string; 
  onShowConnectModal: () => void;
  onDisconnectWallet?: () => void;
  onGoHome?: () => void;
  isOnPlatformPage?: boolean;
  onSelectTradingWallet?: (walletPublicKey: string) => void;
  onNavigateToWallets?: () => void;
  onDropdownOpen?: () => void;
}

// Standardized section header component for consistency - more compact
const SectionHeader = ({ title, children }: { title: string, children?: React.ReactNode }) => (
  <div className="flex items-center justify-between mb-1">
    <span className="text-xs text-gray-400 font-medium">{title}</span>
    {children}
  </div>
);

// Move TokenItem before the WalletConnection component
const TokenItem: React.FC<{
  mint: string, 
  symbol: string | null, 
  name: string | null, 
  balance: string,
  usdValue?: number,
  imageUri?: string | null,
  lastUpdated?: number,
  className?: string
}> = ({ mint, symbol, name, balance, usdValue, imageUri, lastUpdated, className }) => {
  const [imageError, setImageError] = useState(false);
  
  // Check if it was updated recently (within last 2 seconds)
  const isUpdated = lastUpdated ? Date.now() - lastUpdated < 2000 : false;
  
  // Format balance with appropriate precision based on value
  const formatBalance = (balanceStr: string) => {
    const value = parseFloat(balanceStr);
    if (isNaN(value)) return '0';
    if (value === 0) return '0';
    if (value < 0.0001) return '<0.0001';
    if (value < 0.01) return value.toFixed(4);
    if (value < 1) return value.toFixed(3);
    if (value < 1000) return value.toFixed(2);
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // Format USD value
  const formatUSD = (value?: number) => {
    if (!value) return '';
    if (value < 0.01) return value > 0 ? '<$0.01' : '$0';
    if (value < 1000) return `$${value.toFixed(2)}`;
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };
  
  return (
    <div className={`token-item ${isUpdated ? 'animate-pulse-green' : ''} ${className || ''}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* Token Icon */}
        {imageUri && !imageError ? (
          <div className="w-6 h-6 rounded-full flex-shrink-0 overflow-hidden bg-[#182828] border border-gray-700/30 flex items-center justify-center">
            <img 
              src={imageUri} 
              alt={symbol || mint}
              className="w-5 h-5 object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div className="w-6 h-6 rounded-full bg-[#24c35b]/10 flex items-center justify-center flex-shrink-0 border border-[#24c35b]/20">
            <span className="text-[10px] text-[#24c35b] font-medium">{symbol?.substring(0, 2) || mint.substring(0, 2)}</span>
          </div>
        )}
        
        {/* Token Info */}
        <div className="overflow-hidden min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-900 truncate leading-tight">
            {symbol || mint.slice(0, 6) + "..."}
          </p>
          <p className="text-[10px] text-gray-600 truncate leading-tight">
            {name || "Unknown Token"}
          </p>
        </div>
      </div>
      
      {/* Token Balance */}
      <div className="token-value flex-shrink-0">
        <p className="text-xs text-gray-900 font-medium leading-tight text-right">
          {formatBalance(balance)}
        </p>
        {usdValue !== undefined && (
          <p className="text-[10px] text-gray-600 leading-tight text-right">
            {formatUSD(usdValue)}
          </p>
        )}
      </div>
    </div>
  );
};

const WalletConnection: React.FC<WalletConnectionProps> = ({ 
  className = '', 
  onShowConnectModal, 
  onDisconnectWallet,
  onGoHome,
  isOnPlatformPage = false,
  onSelectTradingWallet,
  onNavigateToWallets,
  onDropdownOpen
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<'connected' | 'disconnected'>('connected');
  const [copied, setCopied] = useState(false);
  const [copiedWallets, setCopiedWallets] = useState<Set<string>>(new Set());
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [walletProvider, setWalletProvider] = useState<'phantom' | 'solflare' | null>(null);
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  const [isLoadingTradingWallets, setIsLoadingTradingWallets] = useState(false);
  const [tradingWalletBalances, setTradingWalletBalances] = useState<{[key: string]: number}>({});
  const [activeWalletKey, setActiveWalletKey] = useState<string | null>(
    localStorage.getItem('activeWalletKey')
  );
  const [displayName, setDisplayName] = useState<string | null>(null);
  const lastRefreshTimeRef = useRef<number>(0);
  const pendingRefreshRef = useRef<NodeJS.Timeout | null>(null);
  // Create a ref for lastUpdateTimes rather than recreating in each effect
  const lastUpdateTimesRef = useRef<Map<string, number>>(new Map());

  const { refreshBalances, solBalance, tokenBalances, isLoading, lastUpdated, solLastUpdated, setWallet } = useWalletBalances();

  // Add these state variables near the other state declarations
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  
  // Add new state variables for tracking token balances with USD values
  const [walletTokensWithMetadata, setWalletTokensWithMetadata] = useState<any[]>([]);
  const [isLoadingTokenMetadata, setIsLoadingTokenMetadata] = useState(false);
  const [activeTradingWalletTokens, setActiveTradingWalletTokens] = useState<any[]>([]);
  const [isLoadingTradingTokenMetadata, setIsLoadingTradingTokenMetadata] = useState(false);
  const [walletTab, setWalletTab] = useState<'main' | 'trading'>('main');
  
  // Add ref for the refresh interval
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add handler to refresh token balances with metadata
  const refreshTokensWithMetadata = async () => {
    if (!walletAddress) return;
    
    setIsLoadingTokenMetadata(true);
    try {
      const response = await getWalletTokensWithMetadata(walletAddress);
      if (response.status === 'success' && response.data && Array.isArray(response.data.tokens)) {
        setWalletTokensWithMetadata(response.data.tokens);
      }
      
      // Also refresh trading wallet tokens if active
      if (activeWalletKey) {
        setIsLoadingTradingTokenMetadata(true);
        const tradingResponse = await getWalletTokensWithMetadata(activeWalletKey);
        if (tradingResponse.status === 'success' && tradingResponse.data && Array.isArray(tradingResponse.data.tokens)) {
          setActiveTradingWalletTokens(tradingResponse.data.tokens);
        }
      }
    } catch (error) {
      console.error("Error refreshing token balances with metadata:", error);
    } finally {
      setIsLoadingTokenMetadata(false);
      setIsLoadingTradingTokenMetadata(false);
    }
  };
  
  // Update fetchTradingWalletBalances to use batch request
  const fetchTradingWalletBalances = useCallback(async (wallets: TradingWallet[]) => {
    const balances: {[key: string]: number} = {};
    
    try {
      if (wallets.length === 0) return;
      
      console.log(`💼 Batch fetching balances for ${wallets.length} trading wallets...`);
      
      // Extract wallet addresses for batch request
      const walletAddresses = wallets.map(wallet => wallet.public_key);
      
      // Use batch request instead of individual requests
      const batchResponse = await getBatchWalletTokensWithMetadata(walletAddresses);
      
      if (batchResponse.status === 'success' && batchResponse.data) {
        // Process batch results
        Object.entries(batchResponse.data).forEach(([walletAddress, walletData]: [string, any]) => {
          if (walletData.tokens && Array.isArray(walletData.tokens)) {
            // Find SOL token in the response
            const solToken = walletData.tokens.find((token: any) => token.symbol === 'SOL');
            balances[walletAddress] = solToken ? parseFloat(solToken.balance) : 0;
          } else {
            balances[walletAddress] = 0;
          }
        });
        
        setTradingWalletBalances(balances);
        console.log(`✅ Successfully batch fetched balances for ${wallets.length} trading wallets!`);
      } else {
        console.warn('Batch request failed, falling back to individual requests...');
        
        // Fallback to individual requests if batch fails
        const balancePromises = wallets.map(async (wallet) => {
          try {
            const response = await getWalletTokensWithMetadata(wallet.public_key);
            if (response.status === 'success' && response.data && Array.isArray(response.data.tokens)) {
              const solToken = response.data.tokens.find(token => token.symbol === 'SOL');
              return {
                walletKey: wallet.public_key,
                balance: solToken ? parseFloat(solToken.balance) : 0
              };
            } else {
              return {
                walletKey: wallet.public_key,
                balance: 0
              };
            }
          } catch (error) {
            console.error(`Error fetching balance for trading wallet ${wallet.public_key}:`, error);
            return {
              walletKey: wallet.public_key,
              balance: 0
            };
          }
        });
        
        const balanceResults = await Promise.all(balancePromises);
        balanceResults.forEach(result => {
          balances[result.walletKey] = result.balance;
        });
        
        setTradingWalletBalances(balances);
        console.log(`✅ Fallback: Successfully fetched balances for ${wallets.length} trading wallets!`);
      }
      
    } catch (error) {
      console.error('Error fetching trading wallet balances:', error);
    }
  }, []);

  // Update handleRefreshBalances to be more resilient
  const handleRefreshBalances = useCallback(async () => {
    if (!walletAddress || refreshCooldown) return;
    
    setIsRefreshing(true);
    setRefreshCooldown(true);
    
    try {
      // Refresh main wallet tokens with metadata
      await refreshTokensWithMetadata();
      
      // Refresh all trading wallet balances using the same method
      await fetchTradingWalletBalances(tradingWallets);
      
      // If we have an active trading wallet, refresh its tokens
      if (activeWalletKey) {
        setIsLoadingTradingTokenMetadata(true);
        try {
          const response = await getWalletTokensWithMetadata(activeWalletKey);
          if (response.status === 'success' && response.data && Array.isArray(response.data.tokens)) {
            setActiveTradingWalletTokens(response.data.tokens);
          }
        } catch (error) {
          console.error("Error refreshing active trading wallet tokens:", error);
        } finally {
          setIsLoadingTradingTokenMetadata(false);
        }
      }
      
      // Trigger wallet_selected event to ensure UI updates
      window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
        detail: {
          walletPublicKey: walletAddress
        }
      }));
      
      // If we have an active trading wallet, also trigger a refresh for it
      if (activeWalletKey) {
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
          detail: {
            walletPublicKey: activeWalletKey
          }
        }));
      }
    } catch (error) {
      console.error("Error refreshing balances:", error);
    } finally {
      setIsRefreshing(false);
      
      // Start cooldown timer
      setTimeout(() => {
        setRefreshCooldown(false);
      }, 5000);
    }
  }, [walletAddress, refreshCooldown, tradingWallets, activeWalletKey, refreshTokensWithMetadata, fetchTradingWalletBalances]);

  // Function to start the background refresh interval
  const startBackgroundRefresh = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Set up new interval
    refreshIntervalRef.current = setInterval(() => {
      if (walletAddress && !refreshCooldown) {
        handleRefreshBalances();
      }
    }, 5000); // 5 seconds
  }, [walletAddress, refreshCooldown, handleRefreshBalances]);

  // Function to stop the background refresh
  const stopBackgroundRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Effect to manage background refresh when dropdown is open
  useEffect(() => {
    if (isOpen && walletAddress) {
      // Immediate refresh when dropdown opens
      handleRefreshBalances();
      // Start background refresh
      startBackgroundRefresh();
    }

    // Cleanup function
    return () => {
      stopBackgroundRefresh();
    };
  }, [isOpen, walletAddress, handleRefreshBalances, startBackgroundRefresh, stopBackgroundRefresh]);

  // Add effect to listen for display name changes
  useEffect(() => {
    // Handler for when display name changes
    const handleDisplayNameChange = (event: CustomEvent<{ displayName: string | null }>) => {
      setDisplayName(event.detail.displayName);
    };
    
    // Handler for transfer completion - refresh both wallets involved
    const handleTransferCompletion = (event: CustomEvent<{ 
      fromWallet: string, 
      toWallet: string, 
      amount: number, 
      tokenMint: string, 
      transferType: string 
    }>) => {
      console.log('🔄 Transfer completed, refreshing wallet dropdown...');
      
      // Force refresh if the dropdown is open and we're viewing one of the affected wallets
      if (isOpen && walletAddress) {
        const { fromWallet, toWallet } = event.detail;
        
        // Check if main wallet or any trading wallet is involved
        const isMainWalletInvolved = fromWallet === walletAddress || toWallet === walletAddress;
        const isTradingWalletInvolved = tradingWallets.some(w => 
          w.public_key === fromWallet || w.public_key === toWallet
        );
        
        if (isMainWalletInvolved || isTradingWalletInvolved) {
          console.log('🔄 Wallet involved in transfer, forcing immediate refresh...');
          
          // Trigger immediate refresh
          handleRefreshBalances();
          
          // Also refresh trading wallet balances
          if (isTradingWalletInvolved) {
            fetchTradingWalletBalances(tradingWallets);
          }
        }
      }
    };
    
    // Listen for custom events
    window.addEventListener('ekko:display_name_changed', handleDisplayNameChange as EventListener);
    window.addEventListener('ekko:transfer_completed', handleTransferCompletion as EventListener);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('ekko:display_name_changed', handleDisplayNameChange as EventListener);
      window.removeEventListener('ekko:transfer_completed', handleTransferCompletion as EventListener);
    };
  }, [isOpen, walletAddress, tradingWallets, handleRefreshBalances, fetchTradingWalletBalances]);

  // Fix the checkConnectionStatus function to return boolean directly instead of Promise
  const checkConnectionStatus = useCallback(async () => {
    try {
      // Check Shyft connection by making a simple API call
      await getBalance('11111111111111111111111111111111');
      
      setNetworkStatus('connected');
      return true;
    } catch (error) {
      console.error("Error checking network connection:", error);
      setNetworkStatus('disconnected');
      return false;
    }
  }, []);

  // Use a separate sync function that returns current network status without API calls
  const isConnected = () => networkStatus === 'connected';

  // Update loadAndSubscribeTradingWallets to use wallet monitor service and wrap in useCallback
  const loadAndSubscribeTradingWallets = useCallback(async (address: string) => {
    if (!address || isLoadingTradingWallets) return;
    
    setIsLoadingTradingWallets(true);
    try {
      
      // Load trading wallets
      const wallets = await getUserTradingWallets(address);
      setTradingWallets(wallets);
      
      // Fetch initial balances
      await fetchTradingWalletBalances(wallets);
      
      // Start monitoring each trading wallet
      for (const wallet of wallets) {
        await monitorWallet(wallet.public_key);
      }
      
      // Check for active trading wallet in user profile
      if (!activeWalletKey) { // Only if not already set
        const profile = await getUserProfile(address);
        if (profile && profile.active_trade_wallet) {
          setActiveWalletKey(profile.active_trade_wallet);
          
          // Notify parent component if needed
          if (onSelectTradingWallet) {
            onSelectTradingWallet(profile.active_trade_wallet);
          }
        }
      }
    } catch (error) {
      console.error("Error loading trading wallets:", error);
    } finally {
      setIsLoadingTradingWallets(false);
    }
  }, [
    activeWalletKey,
    fetchTradingWalletBalances,
    isLoadingTradingWallets,
    onSelectTradingWallet,
    setActiveWalletKey,
    setIsLoadingTradingWallets,
    setTradingWallets
  ]);

  // Modify the unsubscribe function to use wallet monitor service and wrap in useCallback
  const unsubscribeFromTradingWallets = useCallback(async () => {
    // Stop monitoring each trading wallet
    for (const wallet of tradingWallets) {
      await stopMonitoringWallet(wallet.public_key);
    }
    
    setTradingWallets([]);
  }, [tradingWallets]);

  useEffect(() => {
    // Track processing state to prevent infinite loops
    let isHandlingConnect = false;
    let isHandlingDisconnect = false;
    let isHandlingAccountChange = false;

    const setupWalletListeners = (provider: 'phantom' | 'solflare') => {
      const wallet = provider === 'phantom' ? window.solana : window.solflare;
      
      if (!wallet) return;

      const handleConnect = async () => {
        // Prevent re-entry during processing
        if (isHandlingConnect) return;
        isHandlingConnect = true;
        
        try {
          if (wallet.publicKey) {
            const address = wallet.publicKey.toString();
            
            if (address !== walletAddress) {
              setWalletAddress(address);
              setWalletProvider(provider);
              
              // Update wallet connection in user profile
              try {
                await updateWalletConnection(address, provider);
                
                // Fetch user profile to get display name and active wallet
                const profile = await getUserProfile(address);
                if (profile) {
                  if (profile.display_name) {
                    setDisplayName(profile.display_name);
                  } else {
                    setDisplayName(null);
                  }
                  
                  // Check for active trading wallet
                  if (profile.active_trade_wallet) {
                    setActiveWalletKey(profile.active_trade_wallet);
                  } else {
                    // Clear the active wallet key if none is set in the profile
                    setActiveWalletKey(null);
                  }
                }
              } catch (error) {
                console.error("Error updating wallet connection in profile:", error);
              }
              
              // Use Shyft to fetch initial balances
              if (isConnected()) {
                try {
                  // Refresh balances directly
                  await refreshBalances(address);  
                  // Load and subscribe to trading wallets
                  await loadAndSubscribeTradingWallets(address);
                } catch (error) {
                  console.error("Error refreshing wallet balances:", error);
                }
              }
            }
          }
          setIsConnecting(false);
        } finally {
          isHandlingConnect = false;
        }
      };

      const handleDisconnect = async () => {
        // Prevent re-entry during processing
        if (isHandlingDisconnect) return;
        isHandlingDisconnect = true;
        
        try {
          if (walletAddress && isConnected()) {
            // Ensure we clean up all subscriptions
            try {
              await unsubscribeFromTradingWallets();
              
              // Also clear the wallet in the balances state
              setWallet(null);
            } catch (error) {
            }
          }
          
          setWalletAddress(null);
          setWalletProvider(null);
          setIsConnecting(false);
        } finally {
          isHandlingDisconnect = false;
        }
      };

      const accountChangedListener = async (publicKey: any) => {
        // Prevent re-entry during processing
        if (isHandlingAccountChange) return;
        isHandlingAccountChange = true;
        
        try {
          if (walletAddress && isConnected()) {
            try {
              await unsubscribeFromTradingWallets();
            } catch (error) {
            }
          }
          
          if (publicKey) {
            const address = publicKey.toString();
            
            // Only update if the address has changed
            if (address !== walletAddress) {
              setWalletAddress(address);
              setWalletProvider(provider);
              
              // Update wallet connection in user profile
              try {
                await updateWalletConnection(address, provider);
                
                // Fetch user profile to get display name and active wallet
                const profile = await getUserProfile(address);
                if (profile) {
                  if (profile.display_name) {
                    setDisplayName(profile.display_name);
                  } else {
                    setDisplayName(null);
                  }
                  
                  // Check for active trading wallet
                  if (profile.active_trade_wallet) {
                    setActiveWalletKey(profile.active_trade_wallet);
                    } else {
                    // Clear the active wallet key if none is set in the profile
                    setActiveWalletKey(null);
                  }
                }
              } catch (error) {
                console.error("Error updating wallet connection in profile:", error);
              }
              
              if (isConnected()) {
                try {
                  await loadAndSubscribeTradingWallets(address);
                } catch (error) {
                  console.error("Error subscribing to new wallet:", error);
                }
              }
            }
          } else {
            setWalletAddress(null);
            setWalletProvider(null);
            setDisplayName(null);
          }
        } finally {
          isHandlingAccountChange = false;
        }
      };

      if (wallet.isConnected && wallet.publicKey && !isHandlingConnect) {
        // Simulate a connect event to initialize
        handleConnect();
      }

      wallet.on('connect', handleConnect);
      wallet.on('disconnect', handleDisconnect);
      wallet.on('accountChanged', accountChangedListener);

      return () => {
        wallet.removeListener('connect', handleConnect);
        wallet.removeListener('disconnect', handleDisconnect);
        wallet.removeListener('accountChanged', accountChangedListener);
      };
    };

    const cleanupPhantom = window.solana?.isPhantom ? setupWalletListeners('phantom') : undefined;
    const cleanupSolflare = window.solflare ? setupWalletListeners('solflare') : undefined;

    return () => {
      cleanupPhantom?.();
      cleanupSolflare?.();
    };
  }, [isConnected, refreshBalances, setWallet, walletAddress, loadAndSubscribeTradingWallets]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both the button and the dropdown content
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideDropdown = !(target as Element).closest?.('.wallet-dropdown');
      
      if (isOutsideButton && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const connectWallet = async () => {
    onShowConnectModal();
  };

  const disconnectWallet = async () => {
    try {
      if (walletAddress && isConnected()) {
        try {
          // First unsubscribe from all wallet-related updates
          await unsubscribeFromTradingWallets();
          
          // Make sure to reset wallet state
          setWallet(null);
        } catch (error) {
        }
      }
      
      // Disconnect all wallets
      if (window.solana) {
        await window.solana.disconnect();
      }
      
      if (window.solflare) {
        try {
          await window.solflare.disconnect();
        } catch (e) {
          // Ignore errors with Solflare disconnect
        }
      }
      
      setWalletAddress(null);
      setWalletProvider(null);
      setDisplayName(null);
      setIsOpen(false);
      
      // Call the parent's disconnect handler if provided
      if (onDisconnectWallet) {
        onDisconnectWallet();
      }
      
      // Only navigate to homepage after wallet disconnect if we're on the platform page
      if (onGoHome && isOnPlatformPage) {
        onGoHome();
      }
    } catch (error) {
      console.error("Error disconnecting wallet:", error);
    }
  };

  const formatAddress = (address: string): string => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const copyToClipboard = () => {
    if (!walletAddress) return;
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyWalletAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedWallets(prev => new Set([...prev, address]));
    setTimeout(() => {
      setCopiedWallets(prev => {
        const newSet = new Set(prev);
        newSet.delete(address);
        return newSet;
      });
    }, 2000);
  };

  const getWalletInfo = () => {
    switch (walletProvider) {
      case 'phantom':
        return {
          name: 'Phantom Wallet',
          logo: 'https://187760183-files.gitbook.io/~/files/v0/b/gitbook-x-prod.appspot.com/o/spaces%2F-MVOiF6Zqit57q_hxJYp%2Ficon%2FU7kNZ4ygz4QW1rUwOuTT%2FWhite%20Ghost_docs_nu.svg?alt=media&token=447b91f6-db6d-4791-902d-35d75c19c3d1',
          iconClass: 'bg-[#4e44ce]/10',
          textClass: 'text-[#4e44ce]'
        };
      case 'solflare':
        return {
          name: 'Solflare Wallet',
          logo: 'https://www.solflare.com/wp-content/uploads/2024/11/App-Icon.svg',
          iconClass: 'bg-[#FC822B]/10',
          textClass: 'text-[#FC822B]'
        };
      default:
        return {
          name: 'Connected Wallet',
          logo: '',
          iconClass: 'bg-primary/10',
          textClass: 'text-primary'
        };
    }
  };

  const walletInfo = getWalletInfo();

  // Modified network status indicator with animation
  const NetworkStatusIndicator = () => (
    <div className="flex items-center gap-1">
      <div className="relative">
        <div className={`w-1.5 h-1.5 rounded-full ${networkStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
        {networkStatus === 'connected' && (
          <div className="absolute top-0 left-0 w-1.5 h-1.5 rounded-full bg-green-500 animate-ping opacity-75"></div>
        )}
      </div>
      <span>Mainnet</span>
    </div>
  );

  // Save active wallet to localStorage whenever it changes
  useEffect(() => {
    if (activeWalletKey) {
      localStorage.setItem('activeWalletKey', activeWalletKey);
    } else {
      localStorage.removeItem('activeWalletKey');
    }
  }, [activeWalletKey]);

  // Update the activateWallet function to include animation triggers
  const activateWallet = async (walletPublicKey: string, e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      
      const previousWallet = activeWalletKey;
      const newActiveKey = activeWalletKey === walletPublicKey ? null : walletPublicKey;
      
      // If we're activating a wallet, restore its keypair
      if (newActiveKey && walletAddress) {
        const keypair = await restoreTradingWalletKeypair(walletAddress, newActiveKey);
        if (!keypair) {
          throw new Error('Failed to restore trading wallet keypair');
        }
        
        // Set the keypair in websocket service
        wsService.setActiveKeypair(newActiveKey, keypair);
      } else if (previousWallet) {
        // If we're deactivating a wallet, remove its keypair
        wsService.removeActiveKeypair(previousWallet);
      }
      
      // Update the active wallet in the database
      if (walletAddress) {
        const success = await updateActiveTradingWallet(walletAddress, newActiveKey);
        
        if (success) {
          // Only update state after successful DB update
          setActiveWalletKey(newActiveKey);
          
          // ADDED: Immediately refresh token balances for the newly activated wallet
          if (newActiveKey) {
            console.log(`🔄 Refreshing token balances for newly activated trading wallet: ${newActiveKey.slice(0, 8)}...`);
            setIsLoadingTradingTokenMetadata(true);
            try {
              const response = await getWalletTokensWithMetadata(newActiveKey);
              if (response.status === 'success' && response.data && Array.isArray(response.data.tokens)) {
                setActiveTradingWalletTokens(response.data.tokens);
                console.log(`✅ Successfully refreshed ${response.data.tokens.length} token balances for activated trading wallet`);
              } else {
                console.warn('⚠️ No token data received for newly activated trading wallet');
                setActiveTradingWalletTokens([]);
              }
            } catch (refreshError) {
              console.error('❌ Error refreshing token balances for newly activated trading wallet:', refreshError);
              setActiveTradingWalletTokens([]);
            } finally {
              setIsLoadingTradingTokenMetadata(false);
            }
          } else {
            // If deactivating, clear the tokens
            setActiveTradingWalletTokens([]);
          }
          
          // Dispatch event after database is updated
          notifyActiveWalletChanged(previousWallet, newActiveKey);
        } else {
        }
      }
    } catch (error) {
      console.error("Error activating/deactivating trading wallet:", error);
    }
  };
  
  // Update the WalletItem component to use animated elements
  const WalletItem = ({ wallet }: { wallet: TradingWallet }) => (
    <div 
      key={wallet.public_key} 
      className={`trading-wallet-item w-full px-5 py-2 rounded-none transition-colors border-l-4 ${activeWalletKey === wallet.public_key ? 'border-[#24c35b]' : 'border-transparent'} flex items-center bg-white hover:bg-gray-50`}
      data-wallet-id={wallet.public_key}
    >
      {activeWalletKey === wallet.public_key && (
        <div className="active-wallet-indicator"></div>
      )}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="w-6 h-6 rounded-full bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20 flex-shrink-0">
          <span className="text-[10px] font-medium text-[#24c35b]">TW</span>
        </div>
        <div className="overflow-hidden flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-gray-900 truncate leading-tight">
              {wallet.name}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyWalletAddress(wallet.public_key);
              }}
              className="p-0.5 text-gray-600 hover:text-[#24c35b] transition-colors flex-shrink-0"
              title="Copy wallet address"
            >
              {copiedWallets.has(wallet.public_key) ? (
                <Check size={10} className="text-[#24c35b]" />
              ) : (
                <Copy size={10} />
              )}
            </button>
          </div>
          <p className="text-[10px] text-gray-600 font-mono truncate leading-tight">
            {wallet.public_key.slice(0, 4)}...{wallet.public_key.slice(-4)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-xs font-mono text-[#24c35b] whitespace-nowrap">
          {tradingWalletBalances[wallet.public_key] !== undefined 
            ? tradingWalletBalances[wallet.public_key].toFixed(4) 
            : '0.0000'} SOL
        </div>
        <button 
          className="activate-button flex-shrink-0"
          onClick={(e) => {
            activateWallet(wallet.public_key, e);
          }}
          title={activeWalletKey === wallet.public_key ? "Deactivate Trading Wallet" : "Activate Trading Wallet"}
        >
          <span className="activate-button-icon">
            <Power size={10} />
          </span>
          <span className="activate-button-text">
            {activeWalletKey === wallet.public_key ? "Active" : "Activate"}
          </span>
        </button>
      </div>
    </div>
  );

  // Listen for template changes and close dropdown
  useEffect(() => {
    const handleTemplateChange = () => {
      setIsOpen(false);
    };

    const handleForceClose = () => {
      setIsOpen(false);
      // Also reset any internal state that might be causing issues
      setWalletTab('main');
    };

    const handleTradingWalletCreated = () => {
      // Refresh trading wallets when a new one is created
      if (walletAddress) {
        console.log('🔄 New trading wallet created, refreshing wallet list...');
        loadAndSubscribeTradingWallets(walletAddress);
      }
    };

    const handleTradingWalletDeleted = () => {
      // Refresh trading wallets when one is deleted
      if (walletAddress) {
        console.log('🗑️ Trading wallet deleted, refreshing wallet list...');
        loadAndSubscribeTradingWallets(walletAddress);
      }
    };

    window.addEventListener('ekko:close_all_dropdowns', handleTemplateChange);
    window.addEventListener('ekko:force_close_dropdowns', handleForceClose);
    window.addEventListener('ekko:trading_wallet_created', handleTradingWalletCreated);
    window.addEventListener('ekko:trading_wallet_deleted', handleTradingWalletDeleted);
    
    return () => {
      window.removeEventListener('ekko:close_all_dropdowns', handleTemplateChange);
      window.removeEventListener('ekko:force_close_dropdowns', handleForceClose);
      window.removeEventListener('ekko:trading_wallet_created', handleTradingWalletCreated);
      window.removeEventListener('ekko:trading_wallet_deleted', handleTradingWalletDeleted);
    };
  }, [walletAddress, loadAndSubscribeTradingWallets]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Add custom wallet styles */}
      <style>{walletConnectionStyles}</style>
      
      {walletAddress ? (
        <button 
          onClick={() => {
            if (!isOpen && dropdownRef.current) {
              const rect = dropdownRef.current.getBoundingClientRect();
              const dropdownWidth = 340; // Width of the dropdown
              
              // Calculate left position, ensuring dropdown doesn't go off-screen
              let leftPos = rect.right - dropdownWidth; // Align right edge of dropdown with right edge of button
              if (leftPos < 20) {
                leftPos = 20; // Minimum margin from left edge
              }
              if (leftPos + dropdownWidth > window.innerWidth - 20) {
                leftPos = window.innerWidth - dropdownWidth - 20; // Ensure it fits on screen
              }
              
              setDropdownPosition({
                left: leftPos,
                top: rect.bottom + 4
              });
              
              // Close other dropdowns when opening wallet dropdown
              onDropdownOpen?.();
            }
            setIsOpen(!isOpen);
          }}
          className="wallet-button px-3 py-1.5 text-sm font-medium text-gray-900 flex items-center gap-2 glassmorphism border border-green-200/50 rounded-lg transition-colors"
          type="button"
        >
          <div className="flex items-center gap-2">
            <div className="relative w-2.5 h-2.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#24c35b]"></div>
              <div className="absolute top-0 left-0 w-2.5 h-2.5 rounded-full bg-[#24c35b] animate-ping opacity-75"></div>
            </div>
            <span>{displayName || formatAddress(walletAddress)}</span>
            <ChevronDown size={14} className={`transition-transform text-[#24c35b] ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
      ) : (
        <button 
          onClick={connectWallet}
          className="wallet-button px-3 py-1.5 text-sm font-medium text-gray-900 flex items-center gap-2 glassmorphism border border-green-200/50 rounded-lg transition-colors"
          type="button"
        >
          <Wallet size={16} className="text-[#24c35b]" />
          <span>Connect Wallet</span>
        </button>
      )}

      {isOpen && walletAddress && createPortal(
        <div 
          className="w-[340px]"
          style={{
            position: 'fixed',
            zIndex: 999999,
            left: dropdownPosition.left,
            top: dropdownPosition.top,
            maxHeight: '500px', // Fixed max height instead of viewport-based
            height: 'auto'
          }}
        >
          <div className="wallet-dropdown rounded-2xl overflow-hidden shadow-xl border border-gray-200/30 bg-white">
            {/* Header Section */}
            <div className="bg-white border-b border-gray-200/20 px-5 py-4 flex items-center gap-3">
              {walletInfo.logo ? (
                <div className="w-9 h-9 rounded-lg flex-shrink-0 overflow-hidden border border-[#24c35b]/20 bg-transparent flex items-center justify-center p-0">
                  <img 
                    src={walletInfo.logo}
                    alt={walletInfo.name}
                    className="h-7 w-7 object-contain"
                  />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-lg bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20">
                  <Wallet size={20} className="text-[#24c35b]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-gray-900 truncate">{displayName ? displayName : walletInfo.name}</div>
                <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                  <NetworkStatusIndicator />
                  <span className="font-mono">{formatAddress(walletAddress)}</span>
                  <button 
                    onClick={copyToClipboard}
                    className="text-[#24c35b] hover:text-gray-900 transition-colors ml-1"
                    title="Copy address"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <a 
                    href={`https://solscan.io/account/${walletAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#24c35b] hover:text-gray-900 transition-colors ml-1"
                    title="View on Solscan"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
              <button 
                onClick={handleRefreshBalances}
                className={`text-[#24c35b] hover:text-gray-900 transition-colors p-1 rounded-full ml-2 ${refreshCooldown ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={refreshCooldown}
                title="Refresh balances"
              >
                <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
              </button>
            </div>

            {/* Tabs Section - no gap above, square buttons */}
            <div className="px-0 pt-0 pb-0">
              <div className="flex w-full">
                <button
                  className={`flex-1 py-2 text-sm font-semibold border-none transition-all duration-150 rounded-none ${walletTab === 'main' ? 'bg-[#24c35b] text-white' : 'bg-transparent text-gray-600 hover:text-gray-900'}`}
                  onClick={() => setWalletTab('main')}
                >
                  Main Wallet
                </button>
                <button
                  className={`flex-1 py-2 text-sm font-semibold border-none transition-all duration-150 rounded-none ${walletTab === 'trading' ? 'bg-[#24c35b] text-white' : 'bg-transparent text-gray-600 hover:text-gray-900'}`}
                  onClick={() => setWalletTab('trading')}
                >
                  Trading Wallet {activeTradingWalletTokens.length > 0 && `(${activeTradingWalletTokens.length})`}
                </button>
              </div>
            </div>

            {/* Unified Token List Section (no background, flush with disconnect) */}
            <div className="px-0 pb-0 bg-white">
              <div 
              className="token-balances-section custom-scrollbar overflow-y-auto divide-y divide-gray-200/30"
              style={{ maxHeight: '192px' }} // 12rem = 192px, same as max-h-48
            >
                {walletTab === 'main' ? (
                  <>
                    {isLoadingTokenMetadata && walletTokensWithMetadata.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-6">
                        <Loader size={18} className="animate-spin text-[#24c35b] mb-2" />
                        <p className="text-xs text-gray-600">Loading token balances...</p>
                      </div>
                    ) : walletTokensWithMetadata.length > 0 ? (
                      walletTokensWithMetadata.map(token => (
                        <TokenItem 
                          key={token.mint}
                          mint={token.mint}
                          name={token.name}
                          symbol={token.symbol}
                          balance={token.balance}
                          usdValue={token.value}
                          imageUri={token.image}
                          lastUpdated={token.lastUpdated}
                          className="bg-white hover:bg-gray-50 w-full px-5 py-2 transition-colors rounded-none"
                        />
                      ))
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-xs text-gray-600">No tokens found in wallet</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {isLoadingTradingWallets ? (
                      <div className="flex flex-col items-center justify-center py-6">
                        <Loader size={18} className="animate-spin text-[#24c35b] mb-2" />
                        <p className="text-xs text-gray-600">Loading trading wallets...</p>
                      </div>
                    ) : tradingWallets.length > 0 ? (
                      <div className="space-y-0">
                        {tradingWallets.map(wallet => (
                          <WalletItem 
                            key={wallet.public_key} 
                            wallet={wallet}
                          />
                        ))}
                        {activeWalletKey && (
                          <>
                            <div className="h-px bg-[#23232a] my-2"></div>
                            {isLoadingTradingTokenMetadata && activeTradingWalletTokens.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-4">
                                <Loader size={16} className="animate-spin text-[#24c35b] mb-2" />
                                <p className="text-xs text-gray-600">Loading trading wallet tokens...</p>
                              </div>
                            ) : activeTradingWalletTokens.length > 0 ? (
                              activeTradingWalletTokens.map(token => (
                                <TokenItem 
                                  key={token.mint}
                                  mint={token.mint}
                                  name={token.name}
                                  symbol={token.symbol}
                                  balance={token.balance}
                                  usdValue={token.value}
                                  imageUri={token.image}
                                  lastUpdated={token.lastUpdated}
                                  className="bg-white hover:bg-gray-50 w-full px-5 py-2 transition-colors rounded-none"
                                />
                              ))
                            ) : (
                              <div className="text-center py-4">
                                <p className="text-xs text-gray-600">No tokens found in trading wallet</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-xs text-gray-600">No trading wallets found</p>
                        {onNavigateToWallets && (
                          <button
                            onClick={() => {
                              onNavigateToWallets();
                              setIsOpen(false);
                            }}
                            className="mt-2 text-xs text-[#24c35b] hover:text-gray-900 transition-colors"
                          >
                            Create Trading Wallet
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Footer/Actions Section - flush with tokens section */}
            <div className="px-4 py-3 border-t border-gray-200/20 bg-white flex items-center justify-end">
              <button 
                onClick={disconnectWallet}
                className="text-sm py-1.5 px-4 flex items-center gap-2 rounded-lg bg-white border border-red-500/20 text-red-500 hover:bg-red-50 transition-colors shadow-none"
              >
                <LogOut size={14} />
                Disconnect
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WalletConnection;